import React, { useMemo, useState } from 'react';
import { Empty } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

//STYLES
import { ContainerSelecaoEmMassa, FooterSelecaoEmMassa } from './styled';

//APP
import { GeoPointFeature } from '../..';
import { AnaliseDescontavel, agruparAnalisesPorProfundidade, calcularSelecaoEmMassa } from './meta';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../../../../../../../Components/ButtonCustom';
import Checkbox from '../../../../../../../../../../../../../../../../Components/CustomUi/checkbox';
import { LineDivider } from '../../../../../../../../../../../../../../../../Components/CustomUi/LineDivider';
import { Warning } from '../../../../../../../../../../../../../../../../Components/CustomUi/warning';

interface PropsConteudo {
    /** Todos os pontos da malha do talhão. */
    pontos_da_malha: GeoPointFeature[]
    /** Grupos adicionados nesta sessão do modal (ainda não salvos). */
    pontos_selecionados_adicionados: any[]
    /** Análises já salvas no formulário. */
    analises_salvas: any[]
    talhao_area: string
    onConfirmar: (pontos: GeoPointFeature[]) => void
}

/**
 * Conteúdo do popover de seleção em massa.
 *
 * Fica separado do invólucro porque em tela cheia o ModalPopOver (que renderiza num
 * portal fora do elemento em fullscreen) não é exibido pelo navegador — nesse caso o
 * mesmo conteúdo é montado num container local.
 *
 * Os checkboxes começam desmarcados: sem nada marcado, a confirmação seleciona a malha
 * inteira, exatamente como o antigo "Selecionar todos os pontos". Marcar uma análise
 * retira os pontos dela da seleção.
 */
export const ConteudoSelecaoEmMassa: React.FC<PropsConteudo> = ({ pontos_da_malha,
    pontos_selecionados_adicionados, analises_salvas, talhao_area, onConfirmar
}) => {

    const [descontadas, setDescontadas] = useState<string[]>([])

    const grupos = useMemo(
        () => agruparAnalisesPorProfundidade(pontos_selecionados_adicionados, analises_salvas),
        [pontos_selecionados_adicionados, analises_salvas]
    )

    const { pontosParaSelecionar, qtd_descontados, grid } = useMemo(() => {
        const marcadas = grupos
            .reduce<AnaliseDescontavel[]>((acc, grupo) => acc.concat(grupo.itens), [])
            .filter((item) => descontadas.includes(item.key))

        return calcularSelecaoEmMassa({
            pontosDaMalha: pontos_da_malha,
            marcadas,
            talhao_area,
        })
    }, [grupos, descontadas, pontos_da_malha, talhao_area])

    function alternarDesconto(key: string) {
        setDescontadas((prev) =>
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        )
    }

    /**
     * Tira o <button> interno do Checkbox da ordem de tabulação.
     *
     * Quem recebe o foco é a linha (role="checkbox"); sem isso o Tab pararia duas vezes
     * por item, sendo que a segunda parada não responde ao Enter. O Checkbox não expõe
     * uma prop de tabIndex, então ajustamos o nó direto.
     */
    function tirarBotaoDaTabulacao(node: HTMLSpanElement | null) {
        node?.querySelector('button')?.setAttribute('tabindex', '-1')
    }

    const temAnalises = grupos.length > 0

    return (
        <ContainerSelecaoEmMassa>
            <Warning type='info'>
                Todos os pontos serão selecionados, exceto os descontados!
            </Warning>
            {temAnalises ? (
                <>
                    <h4 className='selecao__massa__titulo'>Descontar pontos já usados em</h4>

                    <div className='selecao__massa__lista'>
                        {grupos.map((grupo) => (
                            <div className='selecao__massa__grupo' key={grupo.profundidade}>
                                <p className='selecao__massa__profundidade'>{grupo.profundidade}</p>

                                {grupo.itens.map((item) => (
                                    <div
                                        className='selecao__massa__item'
                                        key={item.key}
                                        role='checkbox'
                                        aria-checked={descontadas.includes(item.key)}
                                        tabIndex={0}
                                        onClick={() => alternarDesconto(item.key)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                alternarDesconto(item.key)
                                            }
                                        }}
                                    >
                                        {/* O clique é tratado pela linha; aqui o checkbox é só o indicador visual. */}
                                        <span
                                            className='selecao__massa__checkbox'
                                            aria-hidden='true'
                                            ref={tirarBotaoDaTabulacao}
                                        >
                                            <Checkbox
                                                isChecked={descontadas.includes(item.key)}
                                                trigger={() => { }}
                                                onlyCheckBox
                                            />
                                        </span>
                                        <span
                                            className='selecao__massa__cor'
                                            style={{ backgroundColor: item.cor }}
                                        />
                                        <span className='selecao__massa__nome'>{item.analise_nome}</span>
                                        <span className='selecao__massa__qtd'>
                                            ({item.qtd} {item.qtd === 1 ? 'ponto' : 'pontos'})
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    imageStyle={{ height: 40 }}
                    description={
                        <p style={{ margin: 0, padding: 0, fontSize: 12 }}>
                            Nenhuma análise com pontos para descontar
                        </p>
                    }
                />
            )}

            <LineDivider />

            <FooterSelecaoEmMassa>
                <div className='selecao__massa__resumo'>
                    <strong>
                        {qtd_descontados} {qtd_descontados === 1 ? 'ponto descontado' : 'pontos descontados'}
                    </strong>
                    <span>{grid.toFixed(2)} pontos/ha</span>
                </div>

                <ButtonCustom
                    onClick={() => onConfirmar(pontosParaSelecionar)}
                    disabled={pontosParaSelecionar.length === 0}
                    icon={<FontAwesomeIcon icon={faPlus} />}
                >
                    {`Selecionar ${pontosParaSelecionar.length} ${pontosParaSelecionar.length === 1 ? 'ponto' : 'pontos'}`}
                </ButtonCustom>
            </FooterSelecaoEmMassa>
        </ContainerSelecaoEmMassa>
    )
}
